import { QuantityAdjustment } from '../../graphQL/queries/getSession/types'
import { IRoundingRules } from '../../interfaces'
import addPromotionalQtyToValue from './addPromotionalQtyToValue'

export interface ICalculateNewQuantity {
  originalValue: number
  minQtyAllowed: number
  promotionalQty: number
  inRangeValue: number
  autoAdd: boolean
  freegoodsAsSubitem: boolean
  jumpQty: number | string
  resultFixed: boolean
  resultValue: number
  minQty: number | null
  productQuantity: number
  nextValidValue?: number
  prevValidValue?: number
  quantityAdjustment?: `${QuantityAdjustment}`
  ruleInvalidatedBy?: IRoundingRules | string
  allowTypedZero?: boolean
  /**
   * The quantity the counter matched the rounding rule against — the raw typed
   * entry, before it was floored to a jump multiple.
   *
   * The rule's threshold windows are defined against what the customer keyed in,
   * so the multiple they were aiming at has to be derived from that same number.
   * `resultValue` is the floored one, and a floor that crosses the layer midpoint
   * makes `Math.round` snap to the layer *below* — which would round an entry down
   * that the counter matched in an up-window. Absent on library builds that predate
   * the field, where `resultValue` stands in and the behaviour is unchanged.
   */
  ruleMatchValue?: number
  isValueOutOfRange?: boolean
  /**
   * `originalValue` after the counter's display modifier has been divided out.
   * The two differ only on storefronts that configure `globals.counter.modifierMap`,
   * where the keyed-in number is in display units and every limit is in internal
   * ones. Optional, and `originalValue` stands in when it is absent.
   */
  modifiedValue?: number
  /**
   * The effective ceiling this quantity has to respect — the counter's own cap
   * when it forwarded one, else the product's `maxQty`/`stock`. Optional: when
   * absent no clamping happens and every branch behaves exactly as it did
   * before the cap stage existed.
   */
  cap?: number
  /**
   * Whether the counter already clamped the typed value to the cap. Only
   * meaningful alongside `resultFixed`, which it explains: without it a
   * cap clamp is indistinguishable from any other fixed result, and the
   * caller cannot tell which message to show.
   */
  isClampedToCap?: boolean
}

/**
 * Why the normalized quantity differs from what the caller asked for.
 *
 * The point of naming the stages is message selection: "we rounded your
 * quantity" and "you may buy at most 48" are different statements, and the
 * pipeline is the only place that knows which one is true. Callers switch on
 * this instead of re-deriving intent by comparing numbers, which is what
 * produced the long-standing bug where the generic adjustment toast fired on
 * inputs that had not been adjusted at all.
 */
export enum QuantityAdjustmentReason {
  /** Resolved to exactly what the caller asked for. */
  NONE = 'none',
  /** The counter fixed the result; it was passed through untouched. */
  FIXED = 'fixed',
  /** An explicit typed zero, taken as a request to empty the line. */
  TYPED_ZERO = 'typedZero',
  /** A layer/pallet rounding rule moved the value. */
  ROUNDING_RULE = 'roundingRule',
  /** Raised to the product's minimum quantity. */
  MIN_QTY = 'minQty',
  /** Floored to a whole jump multiple. */
  JUMP_FLOOR = 'jumpFloor',
  /** Lowered to the effective cap after every rounding stage had run. */
  MAX_CAP = 'maxCap',
}

/**
 * The quantity to commit plus the stage that produced it.
 *
 * A union rather than an optional `cap`, because "capped" and "the cap that was
 * applied" are one fact: a `MAX_CAP` result with no number to name is a state the
 * caller cannot act on — it would show no message while the quantity changed
 * underneath the customer. Making it unrepresentable is what forces each producer
 * to decide what it means when no cap resolved.
 */
export type INormalizeQuantityResult =
  | {
      value: number
      reason: Exclude<QuantityAdjustmentReason, QuantityAdjustmentReason.MAX_CAP>
    }
  | {
      value: number
      reason: QuantityAdjustmentReason.MAX_CAP
      cap: number
      /**
       * Whether the ceiling actually resolved a quantity to commit.
       *
       * `true` — a quantity at or below `cap` was committed. Either it was lowered
       * to fit, or it already fitted and only the *request* had breached the cap.
       * Something changed relative to what the customer asked for, so this is the
       * case an alert has to report.
       *
       * `false` — no legal quantity exists at or below `cap`, so the line was left
       * exactly as it was and `value` is its untouched quantity, which may itself
       * sit *above* `cap`. Nothing was adjusted, so the generic "we adjusted your
       * quantity" wording would be a false statement here.
       *
       * The two arrived at the same `MAX_CAP` reason with no way to tell them
       * apart, which meant a caller either stayed silent on a real clamp or
       * claimed an adjustment that never happened. Separating them is what lets
       * one reason serve both.
       */
      clamped: boolean
    }

interface IConditionsAdjustmentProps
  extends Pick<
    ICalculateNewQuantity,
    'nextValidValue' | 'prevValidValue' | 'quantityAdjustment' | 'resultValue' | 'productQuantity'
  > {
  ruleInvalidatedBy?: IRoundingRules
  /**
   * The quantity the two round branches are measured against; see
   * `getRoundingReference`. Absent when no rule was breached, which is the only
   * state in which neither branch may fire — deliberately optional rather than
   * defaulted, so a caller cannot mistake "no rule" for a real reference. `0` is
   * unusable as a sentinel here because it is a value the reference genuinely
   * takes (`Math.round(1 / 5) * 5`), and because `reference < resultValue` holds
   * for every positive quantity, so a leaked `0` would silently select round-down.
   */
  roundingReference?: number
}

/**
 * The quantity the round-up / round-down branches compare against.
 *
 * A rounding rule is a *repeating* step — a layer of 10 puts a threshold window
 * around 10, 20, 30 and so on — but `rule.value` holds only the step itself.
 * Comparing against it therefore answers "is the quantity below one whole layer",
 * not "is it below the layer it is being rounded towards", so every quantity past
 * the first layer took the round-down branch. That is the wrong branch for
 * anything sitting in an up-window: with a layer of 10 and the usual 3-unit
 * thresholds a typed 19 rounded *down* to 16 — not a layer multiple at all, which
 * is the one thing a layer rule exists to guarantee.
 *
 * The multiple nearest the quantity is the reference that actually distinguishes
 * the two windows, and it is the same construction the counter used on the way in:
 * `getRuleInvalidatedBy` measures the value against
 * `Math.round(value / rule.value) * rule.value` to decide a rule was breached at
 * all, and `getNextValidValue`/`getPrevValidValue` both reach the same expression
 * through `roundOrFloorValue` when computing their targets. Reusing it here is what
 * stops cinnamon from disagreeing with the values the counter handed it.
 *
 * Nothing regresses in the other direction. Over every input the counter can
 * produce, the only change is quantities that used to round down now rounding up;
 * no input that rounded up now rounds down, and none falls out of the round
 * branches entirely. That holds because the counter never reports a rule as
 * breached when the nearest multiple is `0` or lands exactly on the value — the
 * only two shapes where this reference would pick a different branch.
 *
 * Falls back to `rule.value`, reproducing the old comparison exactly, when the
 * rule's quantity is non-positive. `sanitizeCartProducts` is what produces that
 * shape: one condition covers all six layer/pallet fields but it then writes
 * *both* rules, so a cart item with only a layer quantity gets a pallet rule of
 * `0`. (`castICatalogItems` gates each rule on its own quantity and never emits
 * one.) The counter itself never selects a zero rule as the breached one, so this
 * is defensive only — but `ruleInvalidatedBy` crosses a config-action boundary, so
 * the guard does not assume that. The `Number.isFinite` clause is belt-and-braces
 * in the same spirit: it changes nothing for `NaN`, which fails both comparisons
 * either way, and only bites on an infinite quantity.
 */
const getRoundingReference = (rule: IRoundingRules, resultValue: number): number => {
  if (!(rule.value > 0) || !Number.isFinite(resultValue)) {
    return rule.value
  }
  return Math.round(resultValue / rule.value) * rule.value
}

interface IConditionsAdjustment {
  condition: (args: IConditionsAdjustmentProps) => boolean
  action: (args: IConditionsAdjustmentProps) => number
}

const conditionsAdjustment: IConditionsAdjustment[] = [
  {
    // If the quantityAdjustment mode is VALIDATE and there is a rule invalidation object,
    // we return the product's current quantity (no automatic adjustment),
    // just as a validation check.
    condition: ({ quantityAdjustment, ruleInvalidatedBy }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.VALIDATE && !!ruleInvalidatedBy,
    action: (args: IConditionsAdjustmentProps) => args.productQuantity
  },
  {
    // If mode is ROUND, and a rule invalidation exists, and the reference quantity
    // is greater than the resulting quantity, use the next valid higher quantity.
    // (E.g., round up to the next allowed quantity value.)
    condition: ({
      quantityAdjustment,
      ruleInvalidatedBy,
      resultValue,
      roundingReference,
    }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.ROUND &&
      !!ruleInvalidatedBy &&
      roundingReference !== undefined &&
      roundingReference > resultValue,
    action: ({ nextValidValue = 0 }: IConditionsAdjustmentProps) => nextValidValue
  },
  {
    // If mode is ROUND, and a rule invalidation exists, and the reference quantity
    // is less than the resulting quantity, use the previous valid (lower) value.
    // (E.g., round down to the previous allowed value.)
    condition: ({
      quantityAdjustment,
      ruleInvalidatedBy,
      resultValue,
      roundingReference,
    }: IConditionsAdjustmentProps) =>
      quantityAdjustment === QuantityAdjustment.ROUND &&
      !!ruleInvalidatedBy &&
      roundingReference !== undefined &&
      roundingReference < resultValue,
    action: ({ prevValidValue = 0 }: IConditionsAdjustmentProps) => prevValidValue
  },
]

const isRuleInvalidatedBy = (maybeRuleInvalidatedBy: unknown): maybeRuleInvalidatedBy is IRoundingRules => {
  return typeof maybeRuleInvalidatedBy === 'object' && maybeRuleInvalidatedBy !== null && 'value' in maybeRuleInvalidatedBy && 'thresholdUp' in maybeRuleInvalidatedBy && 'thresholdDown' in maybeRuleInvalidatedBy
}

interface IFloorToJumpArgs {
  value: number
  jumpQty: number | string
  minQtyAllowed: number
}

/**
 * Floor a quantity to a whole jump multiple, counted from `minQtyAllowed`.
 *
 * Extracted so the jump stage and the cap stage round the same way: a cap is
 * not necessarily a multiple of the jump, and clamping to a raw cap would hand
 * the cart a quantity that is invalid for the very reason the jump stage exists.
 */
const floorToJump = ({ value, jumpQty, minQtyAllowed }: IFloorToJumpArgs): number => {
  if (typeof jumpQty !== 'number' || jumpQty <= 1) return value
  const quantityWithoutMin = value - minQtyAllowed
  const module = quantityWithoutMin % jumpQty
  const adjustedQuantity = module > 0 ? value - module : value
  return adjustedQuantity < minQtyAllowed ? minQtyAllowed : adjustedQuantity
}

/**
 * Calculates the appropriate (validated and/or adjusted) product quantity based on various
 * cart constraints, promotional rules, and session-level configuration/logics.
 *
 * This function integrates a layered approach:
 *   1. Handles fixed results (when result should not be changed)
 *   2. Handles an explicit typed zero as a request to empty the line (opt-in)
 *   3. Handles adjustment-specific logic based on cart mode and invalidation rules
 *   4. Handles minimum quantity requirements
 *   5. Handles jump quantity and promotional quantity calculations
 *   6. Falls back to the given input value when no adjustment applies
 *
 * The cap is not a stage of its own but a wrapper applied to whatever the stages
 * above produce, and it is applied after them on purpose: every one of them can
 * raise a quantity — the jump floor counts multiples from `minQtyAllowed`, a ROUND
 * rule can reach for the next layer, and `minQty` lifts outright — so a cap applied
 * any earlier is one another stage can walk back out of.
 *
 * Two exits deliberately do not go through it. An explicit typed zero (stage 2)
 * cannot exceed a ceiling. A result the counter already clamped (stage 1) was
 * capped there and has been shown to the customer; re-applying the cap would
 * second-guess a decision they have already seen. The counter's other fixed
 * result — the `allowSetMaxvalue` path — *is* capped here, because the ceiling it
 * fixes to is not the customer's maximum.
 *
 * @param args {ICalculateNewQuantity} - An object containing all relevant configuration & values:
 *   - originalValue: The value input by the user or prior state.
 *   - minQtyAllowed: The minimal quantity allowed by configuration/rules.
 *   - inRangeValue: The value clamped inside allowed quantity range.
 *   - jumpQty: The quantity interval (e.g. must buy integer multiples of this).
 *   - autoAdd: If true, auto-add free/promotional goods logic applies.
 *   - freegoodsAsSubitem: If true, promotional qty is handled as sub-item logic.
 *   - promotionalQty: The quantity from active promotion.
 *   - resultFixed: If true, forcibly returns the provided resultValue.
 *   - resultValue: A fixed or "next" value suggested by a rounding/policy rule.
 *   - minQty: Optional absolute minimum quantity for this product.
 *   - productQuantity: Existing quantity of the product in the cart.
 *   - quantityAdjustment: Adjustment type per session/config (QuantityAdjustment enum).
 *   - ruleInvalidatedBy: If set, describes an invalidated rule with round/correction context.
 *   - nextValidValue: Used for rounding up in adjustment scenarios.
 *   - prevValidValue: Used for rounding down in adjustment scenarios.
 *   - allowTypedZero: If true, an explicit typed 0 resolves to 0 instead of being
 *     re-adjusted upwards by the adjustment/minQty/jumpQty rules below.
 *   - isValueOutOfRange: The counter's verdict on the typed value; false means it was
 *     accepted as-is. Used to tell a deliberate 0 from an abandoned edit.
 *   - cap: The effective ceiling, if one is known.
 *   - isClampedToCap: Whether the counter already clamped to the cap.
 *
 * @returns {INormalizeQuantityResult} - The quantity to commit plus the stage that produced it.
 */
export const normalizeQuantity = (args: ICalculateNewQuantity): INormalizeQuantityResult => {
  const {
    originalValue,
    minQtyAllowed,
    inRangeValue,
    jumpQty,
    autoAdd,
    freegoodsAsSubitem,
    promotionalQty,
    resultFixed,
    resultValue,
    minQty,
    ruleInvalidatedBy: maybeRuleInvalidatedBy,
    productQuantity,
    quantityAdjustment,
    nextValidValue,
    prevValidValue,
    allowTypedZero,
    ruleMatchValue,
    isValueOutOfRange,
    modifiedValue,
    cap,
    isClampedToCap,
  } = args

  const ruleInvalidatedBy = isRuleInvalidatedBy(maybeRuleInvalidatedBy) ? maybeRuleInvalidatedBy : undefined

  // A cap of 0 is not treated as a ceiling to clamp against. It means the product
  // is unavailable rather than "buy zero of it", and clamping to it would turn an
  // ordinary add into a line removal. Availability is enforced elsewhere (the
  // counter's disabled state and its out-of-stock actions).
  const effectiveCap = typeof cap === 'number' && Number.isFinite(cap) && cap > 0 ? cap : undefined

  // Whether the quantity that was asked for was over the ceiling, regardless of
  // what the stages below then resolved it to. Compared in internal units: the
  // keyed-in number is in display units and every limit is in internal ones, so on
  // a storefront with a display modifier of 12 a customer asking for two cases
  // keys in 24 against a cap counted in cases. Comparing the two directly would
  // report a breach that never happened.
  const requestedQuantity =
    typeof modifiedValue === 'number' && Number.isFinite(modifiedValue)
      ? modifiedValue
      : originalValue
  const requestExceededCap = effectiveCap !== undefined && requestedQuantity > effectiveCap

  /**
   * Applies the cap to a value one of the stages above produced, keeping the
   * result on a valid jump multiple.
   *
   * The cap also claims the reason when the resolved value fits but the request
   * did not — asking for 49 where 48 is the ceiling and the jump floor happens to
   * land on 48 anyway is still a customer who asked for more than they may buy,
   * and the ceiling is the honest thing to tell them. When neither the value nor
   * the request breached it, the original reason is returned untouched.
   *
   * Only the over-cap branch re-floors. A value already at or below the cap is
   * passed through as its stage produced it, so a stage that hands over an
   * off-step quantity keeps it — see TODO(COM-711).
   */
  const withCap = (
    value: number,
    reason: Exclude<QuantityAdjustmentReason, QuantityAdjustmentReason.MAX_CAP>,
  ): INormalizeQuantityResult => {
    // An unparseable quantity compares false against every bound, so `value <= cap`
    // below reads it as over-cap and clamps it to the ceiling — turning a visibly
    // failed entry into a silently wrong one. Leave the line as it is instead.
    if (!Number.isFinite(value)) {
      return { value: productQuantity, reason: QuantityAdjustmentReason.NONE }
    }
    if (effectiveCap === undefined) {
      return { value, reason }
    }
    if (value <= effectiveCap) {
      return requestExceededCap
        ? { value, reason: QuantityAdjustmentReason.MAX_CAP, cap: effectiveCap, clamped: true }
        : { value, reason }
    }
    const cappedValue = floorToJump({ value: effectiveCap, jumpQty, minQtyAllowed })
    // A cap narrower than one jump leaves no legal quantity at or below it:
    // flooring either lands on `0` — which the cart reads as "empty this line" —
    // or on a `minQtyAllowed` that is itself above the cap, the one thing this
    // stage exists to prevent. Neither is a quantity worth committing, so leave
    // the line as it is and let the reason carry the explanation to the user.
    if (cappedValue <= 0 || cappedValue > effectiveCap) {
      return {
        value: productQuantity,
        reason: QuantityAdjustmentReason.MAX_CAP,
        cap: effectiveCap,
        clamped: false,
      }
    }
    return { value: cappedValue, reason: QuantityAdjustmentReason.MAX_CAP, cap: effectiveCap, clamped: true }
  }

  // 1. Forced/fixed result: return immediately. The counter already resolved this
  // one, cap included when it clamped, so re-applying any stage here would second
  // guess a decision that has already been shown to the user.
  if (resultFixed) {
    if (!Number.isFinite(resultValue)) {
      return { value: productQuantity, reason: QuantityAdjustmentReason.NONE }
    }
    // `isClampedToCap` is compared strictly. It arrives through the config action
    // args, where an unresolved `{{this.isClampedToCap}}` placeholder — a counter on
    // a library build that predates the binding — resolves to the literal template
    // string, which is truthy. Anything other than a real `true` therefore has to
    // read as "not clamped", or a storefront would start naming a ceiling on fixed
    // results that never hit one. (An SCS override that drops the key leaves it
    // `undefined`, which is falsy and needs no special handling.)
    if (isClampedToCap === true) {
      // The counter already clamped and showed the result, so it is committed as
      // is. Reporting it as capped needs a number to name; without one the honest
      // answer is that the counter fixed the value, not that a ceiling applied.
      return effectiveCap !== undefined
        ? { value: resultValue, reason: QuantityAdjustmentReason.MAX_CAP, cap: effectiveCap, clamped: true }
        : { value: resultValue, reason: QuantityAdjustmentReason.FIXED }
    }
    // The other producer of `resultFixed` is the counter's `allowSetMaxvalue` path,
    // whose own ceiling is the *larger* of stock and the cap — so a value it fixed
    // can still sit above the customer's maximum and does need the cap stage.
    return withCap(resultValue, QuantityAdjustmentReason.FIXED)
  }

  // 2. An explicit typed zero means "I want none of this", so return 0 — which the
  // caller turns into a removal — rather than letting the session adjustment, minQty
  // or jumpQty rules below pull it back to a non-zero quantity. Without this, the same
  // gesture resolves several different ways depending on the product's config. Note
  // this deliberately takes precedence over quantityAdjustment VALIDATE/ROUND too.
  //
  // Both operands matter. `originalValue` is the raw typed number, but a field holding
  // no parseable number coerces to 0 as well, so it alone cannot tell a deliberate zero
  // from an abandoned edit. `isValueOutOfRange` is the counter's own verdict on the
  // value: false means it accepted this as a real 0. Checking the verdict rather than
  // comparing `inRangeValue` to 0 matters when the line's current quantity is already
  // 0, because then a rejected value also reverts to 0 and would look accepted.
  if (allowTypedZero && originalValue === 0 && !isValueOutOfRange) {
    return { value: 0, reason: QuantityAdjustmentReason.TYPED_ZERO }
  }

  // 3. Check for adjustment logic based on cart/session/invalidated round rules
  const argsAdjustment = {
    nextValidValue,
    prevValidValue,
    quantityAdjustment,
    ruleInvalidatedBy,
    productQuantity,
    resultValue,
    // Derived from the quantity the rule was matched against, but still compared
    // against `resultValue` below: the reference is the multiple the customer was
    // aiming at, and the direction is whether that target sits above or below the
    // quantity that would otherwise be committed.
    roundingReference: ruleInvalidatedBy
      ? getRoundingReference(
          ruleInvalidatedBy,
          typeof ruleMatchValue === 'number' && Number.isFinite(ruleMatchValue)
            ? ruleMatchValue
            : resultValue,
        )
      : undefined,
  }

  const conditionAdjustment = conditionsAdjustment.find((condition) => condition.condition(argsAdjustment))

  if (conditionAdjustment) {
    const adjustedValue = conditionAdjustment.action(argsAdjustment)
    // A ROUND rule that resolves to the value it started from has not moved
    // anything — the counter's target is bounded by the cap, so it hands back the
    // quantity unchanged whenever the next multiple sits above the ceiling. Fall
    // through to the stages below rather than returning, so whatever *did* move
    // the quantity still gets reported: `inRangeValue` arrives already floored, so
    // returning here would call a jump-floored entry "unchanged" and the customer
    // would be given fewer units than they asked for with nothing said about it.
    // VALIDATE is deliberately excluded — there the unchanged value is the point.
    const ruleMovedNothing =
      quantityAdjustment === QuantityAdjustment.ROUND &&
      adjustedValue === resultValue
    if (!ruleMovedNothing) {
    // The counter bounds its own targets by `max`, so `getNextValidValue` returns
    // the quantity untouched when the next multiple sits above the ceiling. The
    // branch still fires, but nothing moved — and reporting `ROUNDING_RULE` there
    // makes the caller tell the customer "we adjusted your quantity" about the
    // number they typed. Same false statement the jump stage avoids with
    // `landedOnRequest` and the cap stage with `clamped`.
      return withCap(adjustedValue, QuantityAdjustmentReason.ROUNDING_RULE)
    }
  }

  // 4. Handle explicit minQty requirement if higher than previous and applies
  if (
    originalValue !== minQtyAllowed &&
    typeof minQty === 'number' &&
    minQty > originalValue
  ) {
    return withCap(minQty, QuantityAdjustmentReason.MIN_QTY)
  }

  // 5. JumpQty logic (for bulk/step purchases)
  if (typeof jumpQty === 'number' && jumpQty > 1) {
    const updatedOriginalValue = addPromotionalQtyToValue({
      valueToUpdate: originalValue,
      promotionalQty,
      freegoodsAsSubitem,
      autoAdd,
    })

    const finalAdjustedQuantity = floorToJump({
      value: updatedOriginalValue,
      jumpQty,
      minQtyAllowed,
    })

    // Measured against what the customer asked for, not against the intermediate.
    // `updatedOriginalValue` has the promotional quantity folded in, so on a
    // free-goods product the floor routinely lands back on the typed number after
    // trimming the promo units — the customer's own quantity, reported as an
    // adjustment they never made. Comparing against `originalValue` as well keeps
    // that silent, while a floor that really did move the typed value still says so.
    const landedOnRequest =
      finalAdjustedQuantity === updatedOriginalValue ||
      finalAdjustedQuantity === originalValue

    return withCap(
      finalAdjustedQuantity,
      landedOnRequest
        ? QuantityAdjustmentReason.NONE
        : QuantityAdjustmentReason.JUMP_FLOOR,
    )
  }

  // 6. Fallback: return the calculated/clamped in-range value
  return withCap(inRangeValue, QuantityAdjustmentReason.NONE)
}

export default normalizeQuantity
